<script setup lang="ts">
import type { SectionStatuses, UseFinancingData } from '~/store-v2/financings/useFinancing'
import type { StageFormalization, Step, Tag } from '~/ui-blocks/Formalization/Formalization.types'
import type { User } from '~/utils/auth/User'
import { SolSteps, SolTag } from '@solfacil/girassol'
import IconClock from '~icons/material-symbols/schedule-outline'
import IconAlert from '~icons/material-symbols/warning-outline'
import TurboPrimeTag from '~/components/turbo-prime/TurboPrimeTag.vue'
import { STATUSES_MAPPING } from '~/services-v2/financial-bff/financial-bff.consts'
import useFinancingSystemType from '~/store-v2/financings/useFinancingSystemType'
import date from '~/utils/date'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'
import { isOnlyBattery, systemTypeLabelKey } from '~/utils/project/systemType'

const props = defineProps<{
  title?: string
  stage?: string
  status?: string
  tagPage?: Tag
  projectStatus?: UseFinancingData
  bnplStatus?: string
  isInactiveAndNotComplete?: boolean
  financing?: UseFinancingData
  systemPower?: number
  isTurboPrime?: boolean
}>()

const emit = defineEmits<{ (e: 'stage', v: StageFormalization) }>()

const { t } = useI18n()
const user: User = useAppStorage().get('user')
const config = import.meta.env

const tag = ref<Tag>({ text: '', type: 'neutral' })
const PROPOSAL_STEPS = [
  'proposal',
  'customer_data',
  'equipment',
  'payment_model',
  'biometry_and_contract',
]

const generatedProposalUrl = computed(() => {
  if (!props.financing)
    return ``

  const { project_id, person_type } = props.financing
  return `/${person_type === 'PF' ? 'customer' : 'company'}/${project_id}`
})

const isReprovedSimulation = computed(() => props.financing?.status === 'reproved' && props.financing?.step === 'simulation')

const stepData = computed(() => [
  {
    key: 'simulation',
    text: t('customer.header.steps.simulation'),
    current: props.financing ? props.financing?.step === 'simulation' : true,
    href: isReprovedSimulation.value ? '' : `/simulation/details/${props.financing?.project_id}`,
  },
  {
    key: 'proposal',
    text: t('header.bnpl.steps.formalization'),
    current: props.financing ? PROPOSAL_STEPS.includes(props.financing?.step) : false,
    href: generatedProposalUrl.value,
  },
  {
    key: 'installation',
    text: t('customer.header.steps.installation'),
    current: props.financing ? props.financing?.step === 'installation' && props.financing?.status !== 'approved' : false,
    href: `/installation/${props.financing?.project_id}`,
  },
])

function simpleName(name: string) {
  const ignoredWords = ['de', 'da', 'do', 'dos', 'das', 'e', 'ou', 'para', 'com']
  const words = name.toLowerCase().split(' ')
  const formattedWords = words.map((word, index) => {
    if (index === 0 || index === words.length - 1)
      return word.charAt(0).toUpperCase() + word.slice(1)

    return ignoredWords.includes(word) ? null : `${word.charAt(0).toUpperCase()}.`
  }).filter(Boolean)

  return formattedWords.join(' ')
}

const stageTitle = computed(() => simpleName(props.title ?? '') || t({
  bnpl: 'simulation.new',
  simulation: 'simulation.new',
  proposal: 'customer.new',
  installation: 'installationPage.messageNewInstallation',
}[props.financing?.step || 'simulation'] || 'simulation.new'))

const isAdmin = computed(() => props.financing?.step === 'simulation' && user.perfil === 'admin')
const isIntegradorLight = computed(() => props.financing?.step === 'simulation' && user.perfil === 'integrador_light')

const stepActions: Record<string, () => void> = {
  simulation: setSimulationStatus,
  proposal: setStatusFormalization,
  installation: setStatusInstallation,
  bnpl: setBnplStatus,
}

const externalMapping: Record<string, { textKey: string, color: string }> = {
  canceled: STATUSES_MAPPING.canceled,
  expired: STATUSES_MAPPING.expired,
}

watch(
  () => props.financing,
  (financing) => {
    if (!financing)
      return

    const step = props.stage
    if (step && stepActions[step]) {
      stepActions[step]()
    }
  },
  { immediate: true },
)

function getStepByKey(value: string): Step {
  return (stepData.value.find(step => step.key === value) as Step) || {}
}

function setStatusFormalization() {
  if (!props.financing)
    return

  if (props.financing?.new_journey === false)
    window.open(`${config.VITE_URL_LEGACY}/financiamento/etapas/${props.financing.id}`)

  const {
    status: financingStatus,
    step: financingStep,
    section_statuses,
  } = props.financing

  const proposalStep = getStepByKey('proposal')
  const installationStep = getStepByKey('installation')

  if (externalMapping[financingStatus]) {
    if (['canceled', 'expired'].includes(financingStatus)) {
      proposalStep.failed = PROPOSAL_STEPS.includes(financingStep)
      proposalStep.current = PROPOSAL_STEPS.includes(financingStep)
      installationStep.current = financingStep === 'installation'
      installationStep.failed = financingStep === 'installation'
    }
    if (PROPOSAL_STEPS.includes(financingStep)) {
      tag.value = {
        text: t(externalMapping[financingStatus].textKey),
        type: externalMapping[financingStatus].color,
      } as Tag
      return
    }
  }

  if (!section_statuses)
    return

  if (section_statuses) {
    emitStage(section_statuses as unknown as SectionStatuses)
  }

  const { customer_data, equipment, payment_model, biometry_and_contract } = section_statuses
  const steps = [customer_data, biometry_and_contract, equipment, payment_model]

  const conditions = [
    {
      check: () => steps.every(s => s.status === GeneralStatuses.APPROVED),
      mapping: STATUSES_MAPPING.approved,
    },
    {
      check: () => {
        const isReproved = steps.some(s => s.status === GeneralStatuses.REPROVED)
        if (isReproved) {
          proposalStep.failed = PROPOSAL_STEPS.includes(financingStep)
          proposalStep.current = PROPOSAL_STEPS.includes(financingStep)
          installationStep.current = financingStep === 'installation'
          installationStep.failed = financingStep === 'installation'
          installationStep.href
            = financingStep === 'installation' ? `/installation/${props.financing?.project_id}` : ''
        }
        return isReproved
      },
      mapping: STATUSES_MAPPING.reproved,
    },
    {
      check: () => biometry_and_contract.status === GeneralStatuses.WAITING_CUSTOMER,
      mapping: STATUSES_MAPPING.waiting_customer,
    },
    {
      check: () => props.financing?.step === 'simulation' && isIntegradorLight.value,
      mapping: { textKey: 'header.project.int_light', color: 'positive' },
    },
    {
      check: () => props.financing?.step === 'simulation' && isAdmin.value,
      mapping: { textKey: 'header.project.admin', color: 'positive' },
    },
  ]
  for (const { check, mapping } of conditions) {
    if (check()) {
      tag.value = { text: t(mapping.textKey), type: mapping.color } as Tag
      return
    }
  }

  tag.value = {
    text: t(STATUSES_MAPPING[financingStatus].textKey),
    type: STATUSES_MAPPING[financingStatus].color,
  } as Tag
}

function setStatusInstallation() {
  if (!props.financing)
    return

  const { status: financingStatus, step: financingStep } = props.financing
  const proposalStep = getStepByKey('proposal')
  const installationStep = getStepByKey('installation')

  if (externalMapping[financingStatus]) {
    if (['canceled', 'expired'].includes(financingStatus)) {
      proposalStep.failed = false
      proposalStep.current = false
      installationStep.current = false
      installationStep.failed = true
    }
    if (financingStep === 'installation') {
      tag.value = {
        text: t(externalMapping[financingStatus].textKey),
        type: externalMapping[financingStatus].color,
      } as Tag
      return
    }
  }

  if (props.status) {
    tag.value = {
      text: t(`installationPage.barInstallation.${props.status}.text`),
      type: t(`installationPage.barInstallation.${props.status}.type`),
    } as Tag
  }
}

function setBnplStatus() {
  if (props.bnplStatus) {
    tag.value = {
      text: t(`header.bnpl.status.${props.bnplStatus}.text`),
      type: t(`header.bnpl.status.${props.bnplStatus}.type`),
    } as Tag
  }
}

function setSimulationStatus() {
  if (!props.financing)
    return

  // Precisa validar aqui os status
  const {
    status: financingStatus,
    step: financingStep,
    section_statuses: { simulation } = {},
  } = props.financing

  const simulationStep = getStepByKey('simulation')
  const proposalStep = getStepByKey('proposal')
  const installationStep = getStepByKey('installation')

  if (externalMapping[financingStatus]) {
    if (['canceled', 'expired'].includes(financingStatus)) {
      proposalStep.failed = PROPOSAL_STEPS.includes(financingStep)
      proposalStep.current = PROPOSAL_STEPS.includes(financingStep)
      installationStep.current = financingStep === 'installation'
      installationStep.failed = financingStep === 'installation'
      installationStep.href
        = financingStep === 'installation'
          ? `/installation/${props.financing?.project_id}`
          : ''
    }
    if (financingStep === 'simulation') {
      tag.value = {
        text: t(externalMapping[financingStatus].textKey),
        type: externalMapping[financingStatus].color,
      } as Tag
      return
    }
  }

  if (PROPOSAL_STEPS.includes(financingStep) && financingStatus === 'reproved') {
    proposalStep.failed = PROPOSAL_STEPS.includes(financingStep)
    proposalStep.current = PROPOSAL_STEPS.includes(financingStep)
    installationStep.current = financingStep === 'installation'
    installationStep.failed = financingStep === 'installation'
    installationStep.href
      = financingStep === 'installation'
        ? `/installation/${props.financing?.project_id}`
        : ''
  }

  if (simulation) {
    if (simulation.status === 'reproved') {
      simulationStep.failed = true
      simulationStep.current = true
      tag.value = {
        text: t(STATUSES_MAPPING.reproved.textKey),
        type: t(STATUSES_MAPPING.reproved.color),
      } as Tag
      return
    }
    if (simulation.status === 'approved') {
      tag.value = {
        text: t(STATUSES_MAPPING.approved.textKey),
        type: t(STATUSES_MAPPING.approved.color),
      } as Tag
    }
  }
}

function emitStage(financing: SectionStatuses) {
  if (!financing)
    return

  const stageConditions: Array<{ status: GeneralStatuses, stage: keyof SectionStatuses }> = [
    { status: GeneralStatuses.REPROVED, stage: 'customer_data' },
    { status: GeneralStatuses.TO_REVIEW, stage: 'customer_data' },
    { status: GeneralStatuses.IN_PROGRESS, stage: 'customer_data' },
    { status: GeneralStatuses.UNDER_ANALYSIS, stage: 'customer_data' },

    { status: GeneralStatuses.REPROVED, stage: 'biometry_and_contract' },
    { status: GeneralStatuses.TO_REVIEW, stage: 'biometry_and_contract' },
    { status: GeneralStatuses.IN_PROGRESS, stage: 'biometry_and_contract' },
    { status: GeneralStatuses.WAITING_CUSTOMER, stage: 'biometry_and_contract' },
    { status: GeneralStatuses.UNDER_ANALYSIS, stage: 'biometry_and_contract' },

    { status: GeneralStatuses.REPROVED, stage: 'equipment' },
    { status: GeneralStatuses.TO_REVIEW, stage: 'equipment' },
    { status: GeneralStatuses.IN_PROGRESS, stage: 'equipment' },
    { status: GeneralStatuses.UNDER_ANALYSIS, stage: 'equipment' },

    { status: GeneralStatuses.REPROVED, stage: 'payment_model' },
    { status: GeneralStatuses.TO_REVIEW, stage: 'payment_model' },
    { status: GeneralStatuses.IN_PROGRESS, stage: 'payment_model' },
    { status: GeneralStatuses.UNDER_ANALYSIS, stage: 'payment_model' },
  ]

  const condition = stageConditions.find(({ status, stage }) => financing[stage]?.status === status)
  if (condition) {
    emit('stage', condition.stage as StageFormalization)
  }
}

const isProjectIsConcluded = computed(() =>
  (props.financing?.status as string) === 'approved',
)

const timeToExpire = computed(() => {
  if (!props.financing?.expires_at || props.financing?.step === 'installation')
    return null

  const expirationDate = date(props.financing.expires_at).startOf('day')
  const today = date().startOf('day')
  const daysUntilExpiration = expirationDate.diff(today, 'day')

  return daysUntilExpiration
})

const isExpired = computed(() => {
  return (props.financing?.formalized_at && props.financing?.step !== 'installation')
    || props.financing?.status === 'expired'
    || props.financing?.status === 'reproved'
    || props.financing?.status === 'canceled'
})

const showTurboPrime = computed(() =>
  props.stage === 'proposal' && props.isTurboPrime === true,
)

// Separate one-shot query (not polled): pending/error/null just hide "Sistema" and keep "Potência".
const { data: financingSystemType } = useFinancingSystemType(computed(() => props.financing?.project_id))

// "Sistema: <tipo>" only for system types the front knows (null/unknown values are hidden).
const systemTypeLabel = computed(() => {
  const key = systemTypeLabelKey(financingSystemType.value)
  return key ? t(key) : ''
})

// only_battery: `system_power` carries the battery capacity (kWh).
const isOnlyBatteryFinancing = computed(() => isOnlyBattery(financingSystemType.value))
</script>

<template>
  <header class="header">
    <div class="title" :class="{ 'my-3': !financing?.project_id }">
      <div class="title-simulation-bar" :title="stageTitle">
        <div>{{ stageTitle }}</div>

        <SolTag
          v-if="tagPage?.text || tag.text"
          id="tag-header-bar"
          :text="tagPage?.text || tag.text"
          :type="tagPage?.type || tag.type"
          size="medium"
          class="mt-1"
        />
        <FinancingsComboTag
          v-if="financing?.is_combo || financing?.is_combo_connect"
          id="tag-combo"
          connect-id="tag-combo-connect"
          :is-combo="financing?.is_combo"
          :is-combo-connect="financing?.is_combo_connect"
          :combo-text="t('header.project.combo')"
          :combo-connect-text="t('header.project.combo_connect')"
          size="medium"
          class="mt-1"
        />
        <TurboPrimeTag v-if="showTurboPrime" />
      </div>
      <span
        v-if="financing?.id"
        class="flex items-center text-neutral-low-light text-2xs gap-1"
      >
        {{ t(financing.step === 'bnpl'
          ? 'simulation.bnpl.solfacilInstallment'
          : 'header.project.title')
        }}
        <span>{{ t('simulation.bnpl.identity') }}: {{ financing.id }} </span>
        <span v-if="systemPower !== null && systemPower !== undefined">
          <template v-if="isOnlyBatteryFinancing">
            | {{ t('header.project.capacity') }}: {{ systemPower.toFixed(2) }} kWh
          </template>
          <template v-else>
            | {{ t('header.project.power') }}: {{ systemPower.toFixed(2) }} kWp
          </template>
        </span>
        <span v-if="systemTypeLabel">
          | {{ t('header.project.system') }}: {{ systemTypeLabel }}
        </span>
        <span
          v-if="!isExpired && !isProjectIsConcluded && timeToExpire && timeToExpire > 0"
          class="flex gap-1 fonts-body-medium-regular text-neutral-low-light"
        > | {{ t('financing.list.headers.expires_at') }}
          <span
            class="flex gap-1" :class="[
              timeToExpire <= 5 ? 'text-feedback-negative-pure' : 'text-neutral-low-dark',
            ]"
          >
            <component
              :is="timeToExpire > 5 ? IconClock : IconAlert"
              class="w-4 h-4"
            />
            {{ timeToExpire }} dias
          </span>
        </span>
      </span>
    </div>
    <div class="steps-container">
      <SolSteps id="package" :steps="stepData" />
    </div>
  </header>
</template>

<style lang="scss" scoped>
.header {
  @apply w-full flex items-center justify-between;
  @apply flex-wrap lg:system:flex-nowrap mb-6;

  .title {
    .title-simulation-bar {
      @apply w-full flex items-center gap-3 truncate my-1;
      @apply justify-between md:system:justify-start;
      @apply fonts-heading-h2
    }
  }

  .steps-container {
    @apply w-full py-2 my-1;

    @screen sx:system {
      @apply w-1/3 py-0;
      @apply bg-brand-secondary-medium;
    }

    @screen lg:system {
      @apply w-100 relative w-[400px];
      right: -35px;
    }
  }
}
</style>
